import React from 'react';
import { View, ViewProps, ViewStyle } from 'react-native';

// Stack component that mimics NativeBase Stack behavior
// Arranges children horizontally (row) or vertically (column) with optional spacing
interface StackProps extends ViewProps {
  // Layout props
  flex?: number;
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';

  // Spacing props
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string; // paddingHorizontal
  paddingY?: number | string; // paddingVertical
  marginX?: number | string; // marginHorizontal
  marginY?: number | string; // marginVertical

  // Background
  bg?: string; // backgroundColor
  backgroundColor?: string;

  // Border
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | number;
  borderRadius?: number;
  borderWidth?: number | string;
  borderColor?: string;

  // Dimensions
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;

  // Stack-specific props
  row?: boolean; // If true, flexDirection = 'row'
  column?: boolean; // If true, flexDirection = 'column'
  spacing?: number; // Space between children
  align?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline'; // Align items
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'; // Justify content

  // NativeBase theme variants (we'll ignore these for now)
  _dark?: any;
  _light?: any;
  _hover?: any;
  _focus?: any;
  _pressed?: any;
  _disabled?: any;
}

const Stack: React.FC<StackProps> = ({
  flex,
  flexDirection,
  justifyContent,
  alignItems,
  alignSelf,

  // Spacing
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingX,
  paddingY,
  marginX,
  marginY,

  // Background
  bg,
  backgroundColor,

  // Border
  rounded,
  borderRadius,
  borderWidth,
  borderColor,

  // Dimensions
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,

  // Stack-specific
  row = false,
  column = false,
  spacing = 0,
  align,
  justify,

  // Theme variants (ignored)
  _dark,
  _light,
  _hover,
  _focus,
  _pressed,
  _disabled,

  children,
  style,
  ...otherProps
}) => {
  // Determine flexDirection based on row/column props
  let finalFlexDirection = flexDirection;
  if (row) {
    finalFlexDirection = 'row';
  } else if (column) {
    finalFlexDirection = 'column';
  }

  // Handle spacing between children
  const childrenWithSpacing = React.Children.toArray(children).map((child, index) => {
    if (index === 0 || spacing === 0) {
      return child;
    }

    const spacingStyle: ViewStyle = {
      [finalFlexDirection === 'row' ? 'marginLeft' : 'marginTop']: spacing,
    };

    return (
      <View key={index} style={spacingStyle}>
        {child}
      </View>
    );
  });

  // Handle rounded prop
  const getBorderRadius = () => {
    if (typeof rounded === 'number') {
      return rounded;
    }
    if (typeof rounded === 'string') {
      switch (rounded) {
        case 'sm':
          return 4;
        case 'md':
          return 8;
        case 'lg':
          return 12;
        case 'xl':
          return 16;
        case 'full':
          return 9999;
        default:
          return 0;
      }
    }
    return borderRadius || 0;
  };

  const stackStyle: ViewStyle = {
    // Stack specific layout
    flexDirection: finalFlexDirection || 'column',
    justifyContent: justify || justifyContent || 'flex-start',
    alignItems: align || alignItems || 'stretch',

    // Flex
    flex,

    // Spacing
    ...(marginTop !== undefined && { marginTop }),
    ...(marginBottom !== undefined && { marginBottom }),
    ...(marginLeft !== undefined && { marginLeft }),
    ...(marginRight !== undefined && { marginRight }),
    ...(paddingTop !== undefined && { paddingTop }),
    ...(paddingBottom !== undefined && { paddingBottom }),
    ...(paddingLeft !== undefined && { paddingLeft }),
    ...(paddingRight !== undefined && { paddingRight }),
    ...(paddingX !== undefined && { paddingHorizontal: paddingX }),
    ...(paddingY !== undefined && { paddingVertical: paddingY }),
    ...(marginX !== undefined && { marginHorizontal: marginX }),
    ...(marginY !== undefined && { marginVertical: marginY }),

    // Background
    backgroundColor: bg || backgroundColor,

    // Border
    borderRadius: getBorderRadius(),
    borderWidth: typeof borderWidth === 'string' ? parseInt(borderWidth, 10) : borderWidth,
    borderColor,

    // Dimensions
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,

    // Align self
    alignSelf,
  };

  return (
    <View style={[stackStyle, style]} {...otherProps}>
      {childrenWithSpacing}
    </View>
  );
};

export default Stack;
